/**
 * Client-side authoritative check for whether a YouTube video is embeddable
 * on this domain. Loads the video in a hidden iframe via the YouTube IFrame
 * API and listens for onError. Codes 101 and 150 are YouTube's official
 * "embedding disabled by the video owner" signals. Codes 100 = video not
 * found / private, 2 = invalid ID.
 *
 * Returns: Promise<{ embeddable: boolean, code: number|null, reason: string|null }>
 */

let ytApiPromise = null;
const loadYouTubeAPI = () => {
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve) => {
    if (window.YT && window.YT.Player) {
      resolve(window.YT);
      return;
    }
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === 'function') prev();
      resolve(window.YT);
    };
    if (!document.querySelector('script[data-yt-api="1"]')) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      tag.setAttribute('data-yt-api', '1');
      document.head.appendChild(tag);
    }
  });
  return ytApiPromise;
};

const ERROR_REASONS = {
  2: 'Invalid YouTube video ID.',
  5: 'HTML5 player error.',
  100: 'Video not found, private, or removed.',
  101: "The video owner has disabled embedding on 3rd-party sites.",
  150: "The video owner has disabled embedding on 3rd-party sites.",
};

export const checkYouTubeEmbeddable = (videoId, timeoutMs = 6000) => {
  return new Promise((resolve) => {
    let settled = false;
    let player = null;
    let host = null;
    const cleanup = () => {
      try { if (player && player.destroy) player.destroy(); } catch (_e) { /* noop */ }
      try { if (host && host.parentNode) host.parentNode.removeChild(host); } catch (_e) { /* noop */ }
    };
    const done = (result) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(result);
    };

    loadYouTubeAPI().then((YT) => {
      // Hidden 1x1 host offscreen
      host = document.createElement('div');
      host.style.position = 'fixed';
      host.style.left = '-9999px';
      host.style.top = '-9999px';
      host.style.width = '1px';
      host.style.height = '1px';
      host.style.opacity = '0';
      host.style.pointerEvents = 'none';
      document.body.appendChild(host);

      const inner = document.createElement('div');
      host.appendChild(inner);

      // Timeout: if no error fires before we consider it embeddable
      const timer = setTimeout(() => {
        done({ embeddable: true, code: null, reason: null });
      }, timeoutMs);

      try {
        player = new YT.Player(inner, {
          videoId,
          playerVars: {
            autoplay: 0,
            controls: 0,
            mute: 1,
            playsinline: 1,
            origin: window.location.origin,
          },
          events: {
            onReady: (e) => {
              // Try to mute + play briefly; if embed is blocked, onError fires
              try { e.target.mute(); e.target.playVideo(); } catch (_e) { /* noop */ }
              // Consider "ready + playing" as embeddable after another short delay
              setTimeout(() => {
                if (!settled) {
                  clearTimeout(timer);
                  done({ embeddable: true, code: null, reason: null });
                }
              }, 1500);
            },
            onError: (e) => {
              clearTimeout(timer);
              const code = e && typeof e.data === 'number' ? e.data : null;
              const reason = code != null && ERROR_REASONS[code]
                ? ERROR_REASONS[code]
                : `YouTube reported playback error (code ${code}).`;
              const embeddable = !(code === 101 || code === 150 || code === 100 || code === 2);
              done({ embeddable, code, reason });
            },
          },
        });
      } catch (_e) {
        clearTimeout(timer);
        done({ embeddable: true, code: null, reason: null });
      }
    });
  });
};
